import React, { useState, useCallback, useRef } from 'react';
import {
    View, StyleSheet, Text, TouchableOpacity,
    Platform, useWindowDimensions, Animated, Modal, ScrollView
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { TabView, SceneMap } from 'react-native-tab-view';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import FeedScreen from './feed/FeedScreen';
import ChatListScreen from './chat/ChatListScreen';
import { useAuth } from '../../contexts/AuthContext';

const SOCIAL_PRIMARY = '#06b6d4';

// Full header height (below safe area)
// Full header height (below safe area)
const HEADER_FULL_H = 44;
const TAB_BAR_H = 48; // Approximate height of the tab bar

/* ═══════════════════════════════════════════════════════════════════════
   Tab bar — static, always visible
═══════════════════════════════════════════════════════════════════════ */
const CustomTabBar = ({ navigationState, setIndex }) => (
    <View style={styles.tabContainer}>
        <View style={styles.tabBar}>
            {navigationState.routes.map((route, i) => {
                const focused = navigationState.index === i;
                return (
                    <TouchableOpacity
                        key={route.key}
                        style={[styles.tabItem, focused && styles.tabItemActive]}
                        onPress={() => setIndex(i)}
                        activeOpacity={0.7}
                    >
                        <Text style={[styles.tabLabel, focused && styles.tabLabelFocused]}>
                            {route.title}
                        </Text>
                    </TouchableOpacity>
                );
            })}
        </View>
    </View>
);

const makeFeedScene = (nav) => () => <FeedScreen navigation={nav} />;
const makeChatScene = (nav) => () => <ChatListScreen navigation={nav} />;

/* ═══════════════════════════════════════════════════════════════════════
   Inner screen
═══════════════════════════════════════════════════════════════════════ */
const SocialScreenInner = ({ navigation }) => {
    const { user } = useAuth();
    const layout = useWindowDimensions();
    const insets = useSafeAreaInsets();
    const [index, setIndex] = useState(0);
    const [isGuidelinesVisible, setGuidelinesVisible] = useState(false);
    const [routes] = useState([
        { key: 'feed', title: 'Feed' },
        { key: 'chats', title: 'Chats' },
    ]);

    // ── Animated header collapse (Native Driver optimized) ────────────────
    const scrollY = useRef(new Animated.Value(0)).current;
    
    // diffClamp for smooth follow-scroll
    const diffClamp = Animated.diffClamp(scrollY, 0, HEADER_FULL_H);

    const headerTranslateY = diffClamp.interpolate({
        inputRange: [0, HEADER_FULL_H],
        outputRange: [0, -HEADER_FULL_H],
        extrapolate: 'clamp',
    });

    const headerOpacity = diffClamp.interpolate({
        inputRange: [0, HEADER_FULL_H * 0.5],
        outputRange: [1, 0],
        extrapolate: 'clamp',
    });

    // We no longer need handleScrollEvent since we're using Animated.event in the child

    // Handle scrolling from child screens
    const renderScene = ({ route }) => {
        switch (route.key) {
            case 'feed':
                return <FeedScreen navigation={navigation} scrollY={scrollY} />;
            case 'chats':
                return <ChatListScreen navigation={navigation} />;
            default:
                return null;
        }
    };

    return (
        <View style={styles.container}>
            {/* ── 1. Fixed Status Bar Background ── */}
            <LinearGradient
                colors={['#0891b2', '#0369a1']}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
                style={[styles.statusBarBg, { height: insets.top }]}
            />

            {/* ── 2. Animated Header & Tabs Block ── */}
            <Animated.View
                style={[
                    styles.animatedHeaderBlock,
                    {
                        top: insets.top,
                        transform: [{ translateY: headerTranslateY }],
                        zIndex: 100,
                    }
                ]}
            >
                {/* Header Content */}
                <LinearGradient
                    colors={['#0891b2', '#0369a1']}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 0 }}
                    style={{ height: HEADER_FULL_H }}
                >
                    <Animated.View style={[styles.headerContent, { opacity: headerOpacity }]}>
                        <View style={styles.headerLeft}>
                            <TouchableOpacity
                                onPress={() => navigation?.navigate?.('UserProfile', { userId: user?.id })}
                                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                            >
                                <Ionicons name="person-circle-outline" size={24} color="#FFFFFF" />
                            </TouchableOpacity>
                        </View>
                        <View style={styles.headerCenter}>
                            <Text style={styles.headerTitle}>Community</Text>
                        </View>
                        <View style={styles.headerRight}>
                            <TouchableOpacity
                                style={styles.actionBtn}
                                onPress={() => setGuidelinesVisible(true)}
                                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                            >
                                <Ionicons name="shield-checkmark-outline" size={22} color="#FFFFFF" />
                            </TouchableOpacity>
                        </View>
                    </Animated.View>
                </LinearGradient>

                {/* Tab Bar Content */}
                <CustomTabBar
                    navigationState={{ index, routes }}
                    setIndex={setIndex}
                />
            </Animated.View>

            {/* ── 3. Feed / Chats content ── */}
            <View style={{ flex: 1, paddingTop: insets.top + TAB_BAR_H }}>
                <TabView
                    navigationState={{ index, routes }}
                    renderScene={renderScene}
                    renderTabBar={() => null}
                    onIndexChange={setIndex}
                    initialLayout={{ width: layout.width }}
                    lazy={false}
                    style={styles.tabView}
                />
            </View>

            {/* ── Community Guidelines Modal ── */}
            <Modal
                visible={isGuidelinesVisible}
                animationType="slide"
                transparent={true}
                onRequestClose={() => setGuidelinesVisible(false)}
            >
                <View style={styles.modalOverlay}>
                    <View style={styles.modalContent}>
                        <View style={styles.modalHeader}>
                            <View style={styles.modalHeaderIconBg}>
                                <Ionicons name="shield-checkmark" size={28} color={SOCIAL_PRIMARY} />
                            </View>
                            <Text style={styles.modalTitle}>Community Guidelines</Text>
                            <TouchableOpacity style={styles.modalCloseBtn} onPress={() => setGuidelinesVisible(false)}>
                                <Ionicons name="close" size={24} color="#6B7280" />
                            </TouchableOpacity>
                        </View>

                        <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.modalScroll}>
                            <Text style={styles.modalIntro}>
                                Our community thrives on mutual respect and positive interactions. To ensure a safe platform, all users must adhere to the following rules while using Posts and Chats.
                            </Text>

                            <View style={styles.ruleItem}>
                                <Ionicons name="heart" size={20} color="#EF4444" style={styles.ruleIcon} />
                                <View style={styles.ruleTextContainer}>
                                    <Text style={styles.ruleTitle}>1. Respectful Communication</Text>
                                    <Text style={styles.ruleDesc}>We have a zero-tolerance policy for hate speech, harassment, bullying, or discrimination of any kind in both public posts and private chats.</Text>
                                </View>
                            </View>

                            <View style={styles.ruleItem}>
                                <Ionicons name="eye-off" size={20} color="#F59E0B" style={styles.ruleIcon} />
                                <View style={styles.ruleTextContainer}>
                                    <Text style={styles.ruleTitle}>2. Appropriate Content</Text>
                                    <Text style={styles.ruleDesc}>Sharing nudity, sexually explicit material, or excessively graphical and violent content is strictly prohibited.</Text>
                                </View>
                            </View>

                            <View style={styles.ruleItem}>
                                <Ionicons name="warning" size={20} color="#3B82F6" style={styles.ruleIcon} />
                                <View style={styles.ruleTextContainer}>
                                    <Text style={styles.ruleTitle}>3. No Spam or Scams</Text>
                                    <Text style={styles.ruleDesc}>Keep the platform safe by not posting malicious links, unauthorized advertisements, spamming behavior, or scam attempts.</Text>
                                </View>
                            </View>

                            <View style={styles.ruleItem}>
                                <Ionicons name="lock-closed" size={20} color="#10B981" style={styles.ruleIcon} />
                                <View style={styles.ruleTextContainer}>
                                    <Text style={styles.ruleTitle}>4. Privacy First</Text>
                                    <Text style={styles.ruleDesc}>Do not share unauthorized personal information or screenshot/forward private messages publicly without mutual consent.</Text>
                                </View>
                            </View>

                            <View style={styles.enforcementBox}>
                                <Text style={styles.enforcementTitle}>🚨 Strict Enforcement 🚨</Text>
                                <Text style={styles.enforcementDesc}>
                                    All reports are actively monitored by our moderation team. Minor offenses will result in temporary suspensions. Severe or repeated violations will immediately lead to permanent account termination.
                                </Text>
                            </View>
                            
                            <TouchableOpacity style={styles.modalAcknowledgeBtn} onPress={() => setGuidelinesVisible(false)}>
                                <Text style={styles.modalAcknowledgeText}>I Understand</Text>
                            </TouchableOpacity>
                        </ScrollView>
                    </View>
                </View>
            </Modal>
        </View>
    );
};

const SocialScreen = ({ navigation }) => (
    <SocialScreenInner navigation={navigation} />
);

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#FFFFFF',
    },

    statusBarBg: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 110,
    },
    animatedHeaderBlock: {
        position: 'absolute',
        left: 0,
        right: 0,
        zIndex: 100,
    },
    headerContent: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 16,
        height: HEADER_FULL_H,
    },

    headerLeft: {
        width: 50,
        alignItems: 'flex-start',
    },
    headerCenter: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
    headerTitle: {
        fontSize: 16,
        fontWeight: '900',
        color: '#FFFFFF',
        letterSpacing: 0.5,
        textTransform: 'uppercase',
    },
    headerRight: {
        width: 50,
        alignItems: 'flex-end',
    },
    actionBtn: {
        padding: 4,
    },

    tabContainer: {
        backgroundColor: '#FFFFFF',
        paddingHorizontal: 14,
        paddingVertical: 3,
        borderBottomWidth: StyleSheet.hairlineWidth,
        borderBottomColor: 'rgba(6,182,212,0.15)',
    },
    tabBar: {
        flexDirection: 'row',
        backgroundColor: '#F3F4F6',
        borderRadius: 20,
        padding: 3,
        height: 36,
    },
    tabItem: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 17,
    },
    tabItemActive: {
        backgroundColor: '#FFFFFF',
        borderWidth: 1,
        borderColor: 'rgba(6,182,212,0.2)',
        ...Platform.select({
            ios: {
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 1 },
                shadowOpacity: 0.08,
                shadowRadius: 2,
            },
            android: { elevation: 2 },
        }),
    },
    tabLabel: {
        fontSize: 13,
        fontWeight: '600',
        color: '#6B7280',
    },
    tabLabelFocused: {
        color: SOCIAL_PRIMARY,
        fontWeight: '700',
    },

    tabView: {
        flex: 1,
        backgroundColor: '#FFFFFF',
    },

    // Modal Styles
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.5)',
        justifyContent: 'flex-end',
    },
    modalContent: {
        backgroundColor: '#FFFFFF',
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
        maxHeight: '85%',
        paddingTop: 20,
    },
    modalHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 20,
        marginBottom: 16,
    },
    modalHeaderIconBg: {
        width: 40,
        height: 40,
        borderRadius: 20,
        backgroundColor: 'rgba(6,182,212,0.1)',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: 12,
    },
    modalTitle: {
        flex: 1,
        fontSize: 18,
        fontWeight: '800',
        color: '#111827',
    },
    modalCloseBtn: {
        padding: 4,
    },
    modalScroll: {
        paddingHorizontal: 20,
        paddingBottom: 40,
    },
    modalIntro: {
        fontSize: 14,
        color: '#4B5563',
        lineHeight: 22,
        marginBottom: 24,
    },
    ruleItem: {
        flexDirection: 'row',
        marginBottom: 20,
    },
    ruleIcon: {
        marginTop: 2,
        marginRight: 12,
    },
    ruleTextContainer: {
        flex: 1,
    },
    ruleTitle: {
        fontSize: 15,
        fontWeight: '700',
        color: '#111827',
        marginBottom: 4,
    },
    ruleDesc: {
        fontSize: 14,
        color: '#6B7280',
        lineHeight: 20,
    },
    enforcementBox: {
        backgroundColor: '#FEF2F2',
        borderWidth: 1,
        borderColor: '#FECACA',
        borderRadius: 16,
        padding: 16,
        marginTop: 10,
        marginBottom: 24,
    },
    enforcementTitle: {
        fontSize: 15,
        fontWeight: '700',
        color: '#DC2626',
        marginBottom: 8,
        textAlign: 'center',
    },
    enforcementDesc: {
        fontSize: 13,
        color: '#991B1B',
        lineHeight: 20,
        textAlign: 'center',
    },
    modalAcknowledgeBtn: {
        backgroundColor: SOCIAL_PRIMARY,
        borderRadius: 24,
        paddingVertical: 14,
        alignItems: 'center',
    },
    modalAcknowledgeText: {
        color: '#FFFFFF',
        fontSize: 16,
        fontWeight: '700',
    },
});

export default SocialScreen;
